export function normalizeInsightsBlock(raw: any): any {
    const d = raw?.data;
    if (!d) return undefined;
    const observations = Array.isArray(d.observations) ? d.observations.filter(Boolean) : [];
    const recommendations = Array.isArray(d.recommendations) ? d.recommendations.filter(Boolean) : [];
    const risks = Array.isArray(d.risks) ? d.risks.filter(Boolean) : [];
    if (observations.length === 0 && recommendations.length === 0 && risks.length === 0) return undefined;
    return {
        moduleName: d.moduleName || d.moduleId || '',
        observations,
        recommendations,
        risks,
        createdAt: d.createdAt,
    };
}

export function normalizeInsights(parts: {
    overview?: any;
    compliance?: any;
    legalChecks?: any;
    auditedFinancials?: any;
    relatedParty?: any;
}): any {
    const overview = normalizeInsightsBlock(parts.overview);
    const compliance = normalizeInsightsBlock(parts.compliance);
    const legalChecks = normalizeInsightsBlock(parts.legalChecks);
    const auditedFinancials = normalizeInsightsBlock(parts.auditedFinancials);
    const relatedParty = normalizeInsightsBlock(parts.relatedParty);
    if (!overview && !compliance && !legalChecks && !auditedFinancials && !relatedParty) return undefined;
    return { overview, compliance, legalChecks, auditedFinancials, relatedParty };
}

function normalizeStatementSlice(name: string, raw: any) {
    const d = raw?.data?.data ?? raw?.data ?? raw;
    if (!d || !Array.isArray(d.headers) || !Array.isArray(d.rows)) return undefined;
    const headers = d.headers.map((h: any) => ({ name: h.name, key: h.key }));
    const sparkYears = headers.filter((h: any) => /^\d{4}$/.test(h.key)).map((h: any) => h.key);
    const rows = d.rows
        .filter((r: any) => r.classificationName && r.classificationName !== 'Reference Document')
        .map((r: any) => {
            const values: Record<string, string> = {};
            headers.forEach((h: any) => {
                if (h.key === 'classificationName') return;
                if (r[h.key] != null) values[h.key] = String(r[h.key]);
            });
            return {
                classificationName: r.classificationName,
                label: r.label,
                subLabel: r.subLabel,
                isAggregated: !!r.isAggregated,
                values,
            };
        });
    if (rows.length === 0) return undefined;
    return { name, headers, rows, sparkYears };
}

export function normalizeAuditedStatements(raw: any): any {
    if (!raw) return undefined;
    const slices: any[] = [];
    let hasStandalone = false;
    let hasConsolidated = false;
    if (Array.isArray(raw.slices)) {
        raw.slices.forEach((s: any) => {
            const slice = normalizeStatementSlice(s.name ?? 'Statement', s.payload);
            if (slice) slices.push(slice);
            const inner = s.payload?.data?.data ?? s.payload?.data;
            if (inner?.hasStandalone) hasStandalone = true;
            if (inner?.hasConsolidated) hasConsolidated = true;
        });
    } else {
        const slice = normalizeStatementSlice('Balance Sheet', raw);
        if (slice) slices.push(slice);
        const inner = raw?.data?.data ?? raw?.data;
        hasStandalone = !!inner?.hasStandalone;
        hasConsolidated = !!inner?.hasConsolidated;
    }
    if (slices.length === 0) return undefined;
    return { statements: slices, hasStandalone, hasConsolidated };
}

// Provisional statements — same slice shape as audited; consumed inside the
// Audited module as a fallback when audited slices aren't available.
export function normalizeProvisionalStatements(raw: any): any {
    if (!raw || !Array.isArray(raw.slices)) return undefined;
    const slices: any[] = [];
    raw.slices.forEach((s: any) => {
        const slice = normalizeStatementSlice(s.name ?? 'Statement', s.payload);
        if (slice) slices.push(slice);
    });
    if (slices.length === 0) return undefined;
    return { statements: slices, hasStandalone: false, hasConsolidated: false };
}

// Banking Overview — per-account snapshot (closing balance, OD limit, LTM
// transactions, missing-days). Source: Polus `data/bankStatementData` returns
// `{ bankData: { bankingOverview: [...], summaryData: [...] } }`. We accept
// either the unwrapped `bankData`, the response root, or the array directly.
export function normalizeBankingOverview(raw: any): any {
    const list = Array.isArray(raw?.bankingOverview)
        ? raw.bankingOverview
        : Array.isArray(raw?.bankData?.bankingOverview)
        ? raw.bankData.bankingOverview
        : Array.isArray(raw)
        ? raw
        : undefined;
    if (!list || list.length === 0) return undefined;
    const accounts = list.map((a: any) => ({
        accountId: a?.accountId,
        name: a?.name,
        accountNumber: a?.accountNumber,
        closingBalance: typeof a?.closingBalance === 'number' ? a.closingBalance : undefined,
        closingBalanceDate: a?.closingBalanceDate ?? null,
        odSanctionLimit: typeof a?.odSanctionLimit === 'number' ? a.odSanctionLimit : undefined,
        transactionsLTM: typeof a?.transactionsLTM === 'number' ? a.transactionsLTM : undefined,
        missingDays: typeof a?.missingDays === 'number' ? a.missingDays : undefined,
    }));
    return accounts.length ? { accounts } : undefined;
}

const parseRupees = (v: string): number => {
    const cleaned = String(v ?? '')
        .replace(/[₹,\s]/g, '')
        .trim();
    if (!cleaned) return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
};

const cleanCharge = (v: any): string | undefined => {
    if (v == null) return undefined;
    const s = String(v).trim();
    if (!s || s === '-' || s === '—') return undefined;
    return s;
};

export function normalizeMcaCharges(raw: any): any {
    const rowData = raw?.graphs?.mcaCharges?.plots?.[0]?.rowData;
    if (!Array.isArray(rowData) || rowData.length === 0) return undefined;
    const rows = rowData.map((entry: any) => {
        const map = new Map<string, string>();
        (entry.amounts ?? []).forEach((a: any) => map.set(a.xValue, a.rowVal));
        const amount = map.get('Charge Amount') ?? '';
        return {
            holder: map.get('Charge Holder Name') ?? '—',
            amount,
            amountNumeric: parseRupees(amount),
            creationDate: map.get('Creation Date') ?? '—',
            modificationDate: cleanCharge(map.get('Modification Date')),
            status: (map.get('Status') ?? '—').toUpperCase(),
            propertyType: map.get('Property Type') ?? '—',
            instrumentDesc: cleanCharge(map.get('Instrument Description')),
            rateOfInterest: cleanCharge(map.get('Rate of Interest')),
            termsOfPayment: cleanCharge(map.get('Terms of Payment')),
            numberOfHolders: cleanCharge(map.get('Number of Holders')),
            propertyParticulars: cleanCharge(map.get('Property Particulars')),
            extentAndOperation: cleanCharge(map.get('Extent and Operation')),
            otherTerms: cleanCharge(map.get('Other Terms')),
        };
    });
    const open = rows.filter((r: any) => r.status === 'OPEN');
    const closed = rows.length - open.length;
    const totalOpenNumeric = open.reduce((s: number, r: any) => s + r.amountNumeric, 0);
    const totalOpenAmountLabel =
        totalOpenNumeric >= 1e7
            ? `₹${(totalOpenNumeric / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`
            : `₹${totalOpenNumeric.toLocaleString('en-IN')}`;
    return { rows, totalOpen: open.length, totalOpenAmountLabel, totalClosed: closed };
}

const DD_PLACEHOLDER =
    /^(\s*(n\/?a|na|none|nil|null|undefined|tbd|—|-+)\s*|\s*data\s+(not\s+(found|available)|unavailable)\s*|\s*not\s+available\s*|\s*no\s+data\s*(available)?\s*)$/i;
const cleanField = (v: any): string | undefined => {
    if (v == null) return undefined;
    const s = String(v).trim();
    if (!s || DD_PLACEHOLDER.test(s)) return undefined;
    return s;
};
const cleanScore = (v: any): number | undefined => {
    if (v == null) return undefined;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return n;
};
// Try every field-name variant we've seen in the wild — different upstream
// services (Karza, MCA, internal) use different keys for the same concepts.
// Without this fallback chain, beginDate/endDate quietly came up empty for
// directors whose source happens to call them startDate/cessationDate.
const cleanEntities = (list: any) =>
    (Array.isArray(list) ? list : [])
        .map((e: any) => ({
            name: cleanField(e?.name) ?? cleanField(e?.entityName) ?? cleanField(e?.companyName) ?? '',
            role: cleanField(e?.role) ?? cleanField(e?.designation),
            beginDate: cleanField(e?.beginDate) ?? cleanField(e?.startDate) ?? cleanField(e?.appointedOn),
            endDate: cleanField(e?.endDate) ?? cleanField(e?.endingDate) ?? cleanField(e?.cessationDate) ?? cleanField(e?.resignedOn),
        }))
        .filter((e: any) => e.name);

type DirectorsDeepInput = any[] | { overviewList?: any[]; detailsList?: any[] } | undefined;

export function normalizeDirectorsDeep(input: DirectorsDeepInput): any {
    if (!input) return undefined;
    const overviewList: any[] = Array.isArray(input) ? [] : Array.isArray(input.overviewList) ? input.overviewList : [];
    const detailsList: any[] = Array.isArray(input) ? input : Array.isArray(input.detailsList) ? input.detailsList : [];

    // Build a map of details keyed by name (case-insensitive) so we can merge later.
    const detailsByName = new Map<string, any>();
    detailsList.forEach((raw) => {
        const p = raw?.responseBody?.managementProfile?.profile;
        if (!p) return;
        const key = (cleanField(p.name) ?? '').toLowerCase().trim();
        if (key) detailsByName.set(key, p);
    });

    // Pick a primary list to drive the order: overview if available, else details (back-compat shape).
    const primary: any[] =
        overviewList.length > 0 ? overviewList : detailsList.map((raw) => raw?.responseBody?.managementProfile?.profile).filter(Boolean);

    // Dedup by directorId or name — overview list can repeat.
    const seen = new Set<string>();
    const profiles: any[] = [];
    primary.forEach((row: any) => {
        const overview = overviewList.length > 0 ? row : null;
        const name = cleanField(row?.name) ?? cleanField(overview?.name) ?? '—';
        const key = (cleanField(row?.directorId) ?? name).toLowerCase().trim();
        if (key && seen.has(key)) return;
        seen.add(key);

        const det = detailsByName.get(name.toLowerCase().trim());
        const score = cleanScore(overview?.cibilScore ?? row?.cibilScore ?? det?.creditScore ?? row?.creditScore);
        const bureau = cleanField(overview?.creditBureau) ?? cleanField(det?.creditBureau) ?? cleanField(row?.creditBureau);
        const dpdAccounts = Number.isFinite(overview?.dpdAccounts) ? overview.dpdAccounts : undefined;
        const totalAccounts = Number.isFinite(overview?.totalAccounts) ? overview.totalAccounts : undefined;
        const currentOverdue = Number.isFinite(overview?.currentOverdue) ? overview.currentOverdue : undefined;
        const maxDpds = Number.isFinite(overview?.maxDpds) ? overview.maxDpds : undefined;
        const enquiriesInPast90Days = Number.isFinite(overview?.enquiriesInPast90Days) ? overview.enquiriesInPast90Days : undefined;

        // Enquiry-window breakdown. Polus sends these as STRINGS in this payload
        // (e.g. "past12Months": "2") — coerce defensively via Number() and then
        // guard with Number.isFinite (skill invariant 25). A non-numeric value
        // becomes undefined rather than NaN so downstream style props can't blow
        // up. `recent` is documented but inconsistently populated — skip it.
        const num = (v: any): number | undefined => {
            if (v == null || v === '') return undefined;
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : undefined;
        };
        const es = overview?.enquirySummary;
        const enquirySummary = es
            ? {
                  past30Days: num(es.past30Days),
                  past12Months: num(es.past12Months),
                  past24Months: num(es.past24Months),
                  total: num(es.total),
              }
            : undefined;
        // Drop the wrapper if every window came back empty — saves the consumer
        // from having to .keys()-filter to detect a no-op summary.
        const hasAnyEnquiry =
            enquirySummary &&
            (enquirySummary.past30Days != null ||
                enquirySummary.past12Months != null ||
                enquirySummary.past24Months != null ||
                enquirySummary.total != null);

        // DPD trend pair (last 12mo vs the 12mo before that). Zero is a real
        // signal here ("clean trajectory") so we keep numeric zeros.
        const last12MonthsDpsInstances = num(overview?.last12MonthsDpsInstances);
        const before12MonthsDpsInstances = num(overview?.before12MonthsDpsInstances);
        const last12MonthsDpsAccounts = num(overview?.last12MonthsDpsAccounts);
        const before12MonthsDpsAccounts = num(overview?.before12MonthsDpsAccounts);

        // Bureau staleness + failure state. lastFetched is epoch-ms; bureauError
        // is a string (or null) sent through cleanField so empty strings drop.
        const lastFetched = Number.isFinite(overview?.lastFetched) ? overview.lastFetched : undefined;
        const bureauError = cleanField(overview?.bureauError);

        profiles.push({
            name,
            designation: cleanField(det?.designation) ?? cleanField(row?.designation) ?? 'Director',
            phone: cleanField(det?.phone),
            pan: cleanField(det?.pan) ?? cleanField(det?.maskedPan),
            gender: cleanField(det?.gender),
            creditScore: score,
            creditBureau: bureau,
            legalCasesCount: Number.isFinite(det?.legalCasesCount) ? det.legalCasesCount : 0,
            currentEntities: cleanEntities(det?.currentEntities),
            formerEntities: cleanEntities(det?.formerEntities),
            dpdAccounts,
            totalAccounts,
            currentOverdue,
            maxDpds,
            enquiriesInPast90Days,
            enquirySummary: hasAnyEnquiry ? enquirySummary : undefined,
            last12MonthsDpsInstances,
            before12MonthsDpsInstances,
            last12MonthsDpsAccounts,
            before12MonthsDpsAccounts,
            lastFetched,
            bureauError,
        });
    });

    return profiles.length ? { profiles } : undefined;
}

// Map an upstream `creditBureau` string ("CIBIL" / "EQUIFAX" / "EXPERIAN" / etc.)
// to the three canonical bureau labels used by the Figma. Anything else falls
// back to CIBIL (the most common upstream default).
const toBureauLabel = (raw: any): 'CIBIL' | 'Equifax' | 'Experian' => {
    const up = String(raw ?? '').toUpperCase();
    if (up.includes('EQUIFAX')) return 'Equifax';
    if (up.includes('EXPERIAN')) return 'Experian';
    return 'CIBIL';
};

const daysAgoFromTimestamp = (ts: any): number | undefined => {
    if (ts == null) return undefined;
    const t = typeof ts === 'number' ? ts : Date.parse(String(ts));
    if (!Number.isFinite(t)) return undefined;
    const diff = Date.now() - t;
    if (diff < 0) return 0;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
};

// Figma 171:4058 expects three bureau rows (CIBIL / Equifax / Experian) per
// director. The upstream `combinedDirectors` API surfaces one bureau row per
// director (the bureau that was actually fetched). We render whichever rows
// the API gives us — never fabricate the missing two.
// TODO: Backend needs per-bureau breakdown to render all 3 rows per Figma 171:4058.
export function normalizeDirectorBureau(input: any): any {
    if (!input) return undefined;
    const overviewList: any[] = Array.isArray(input) ? [] : Array.isArray(input.overviewList) ? input.overviewList : [];
    if (overviewList.length === 0) return undefined;

    const grouped = new Map<string, { name: string; rows: any[] }>();
    overviewList.forEach((row: any) => {
        const name = cleanField(row?.name);
        if (!name) return;
        const score = cleanScore(row?.cibilScore);
        const dpdAccounts = Number.isFinite(row?.dpdAccounts) ? row.dpdAccounts : undefined;
        const currentOverdue = Number.isFinite(row?.currentOverdue) ? row.currentOverdue : undefined;
        const maxDpds = Number.isFinite(row?.maxDpds) ? row.maxDpds : undefined;
        const fetchedDaysAgo = daysAgoFromTimestamp(row?.lastFetched);
        // Skip rows where every numeric field is missing — nothing to render.
        if (score == null && dpdAccounts == null && currentOverdue == null && maxDpds == null) return;

        const key = name.toLowerCase().trim();
        if (!grouped.has(key)) grouped.set(key, { name, rows: [] });
        const bureau = toBureauLabel(row?.creditBureau);
        // Dedup same-bureau within a director (overviewList can repeat).
        const existing = grouped.get(key)!;
        if (existing.rows.some((r) => r.bureau === bureau)) return;
        existing.rows.push({
            bureau,
            score,
            dpdAccounts,
            currentOverdue,
            maxDpds,
            fetchedDaysAgo,
        });
    });

    const directors = Array.from(grouped.values()).filter((d) => d.rows.length > 0);
    return directors.length ? { directors } : undefined;
}

export function normalizeFinancialMetrics(raw: any): any {
    const body = raw?.responseBody;
    const metrics = body?.UW_FETCH_FINANCIAL_METRICS?.data?.data;
    if (!metrics || !Array.isArray(metrics.headers) || !Array.isArray(metrics.rows)) return undefined;
    const headers = metrics.headers
        .filter((h: any) => h.key && h.key !== 'classificationName')
        .map((h: any) => ({ name: h.name ?? '', key: h.key }));
    if (headers.length === 0) return undefined;
    const rows = metrics.rows
        .filter((r: any) => r.metricName)
        .map((r: any) => {
            const values: Record<string, number | string | null> = {};
            headers.forEach((h: any) => {
                values[h.key] = r[h.key] ?? null;
            });
            return { metricName: r.metricName, unit: r.unit, values };
        });
    if (rows.length === 0) return undefined;
    return { headers: [{ name: 'Metric', key: 'metricName' }, ...headers], rows };
}
